import { useMemo, useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import EditIcon from '@mui/icons-material/Edit'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import BarChartIcon from '@mui/icons-material/BarChart'
import ExtensionIcon from '@mui/icons-material/Extension'
import StarIcon from '@mui/icons-material/Star'
import {
  AccountType,
  BillingCycle,
  CreatePlanRequest,
  subscriptionPlanService,
  SubscriptionPlanDto,
  UpdatePlanRequest,
} from '../api/adminServices'
import { formatCurrency } from '../utils/formatCurrency'
import { useLocalizedLabel } from '../utils/getLocalizedLabel'
import { ResizableTableCell, stickyActionsHeaderSx, stickyActionsBodySx } from '../components/ResizableTable'
import { useToast } from '../components/useToast'
import { EmptyState, ErrorCard, TableSkeleton } from '../components/Feedback'
import PageRefreshButton from '../components/PageRefreshButton'
import PlanEntitlementsDialog from '../components/PlanEntitlementsDialog'

interface PlanFormState {
  planCode: string
  name: string
  description: string
  accountType: AccountType
  billingCycle: BillingCycle
  monthlyFee: string
  currency: string
  trialPeriodDays: string
  country: string
  isFeatured: boolean
  sortOrder: string
  features: string
  limitations: string
  // Freighter
  maxJobsPerMonth: string
  perJobCommission: string
  // Shipper
  includedTonsPerMonth: string
  includedAreaM2PerMonth: string
  includedVolumeM3PerMonth: string
  perTonFee: string
  perAreaM2Fee: string
  perVolumeM3Fee: string
  hasPriorityMatching: boolean
}

const emptyForm: PlanFormState = {
  planCode: '',
  name: '',
  description: '',
  accountType: 'SHIPPER',
  billingCycle: 'MONTHLY',
  monthlyFee: '0',
  currency: 'ILS',
  trialPeriodDays: '30',
  country: 'Israel',
  isFeatured: false,
  sortOrder: '0',
  features: '',
  limitations: '',
  maxJobsPerMonth: '',
  perJobCommission: '',
  includedTonsPerMonth: '',
  includedAreaM2PerMonth: '',
  includedVolumeM3PerMonth: '',
  perTonFee: '',
  perAreaM2Fee: '',
  perVolumeM3Fee: '',
  hasPriorityMatching: false,
}

function num(s: string): number | undefined {
  if (s === '' || s == null) return undefined
  const n = Number(s)
  return Number.isFinite(n) ? n : undefined
}

/** Short usage-pricing summary for the plans table (per-ton fees for shippers, per-job for freighters). */
function planPricingSummary(plan: SubscriptionPlanDto): string {
  if (plan.accountType === 'FREIGHTER') {
    const jobs = plan.maxJobsPerMonth != null ? `${plan.maxJobsPerMonth} jobs/mo incl.` : 'Unlimited jobs'
    const comm = plan.perJobCommission != null ? `${formatCurrency(plan.perJobCommission)}/job` : ''
    return [jobs, comm].filter(Boolean).join(' · ')
  }
  // Shipper: included quota + per-ton overage fee (1 ton = 3 m² = 6 m³)
  const incl = plan.includedTonsPerMonth != null ? `${plan.includedTonsPerMonth}t incl.` : ''
  const perTon = plan.perTonFee != null ? `${formatCurrency(plan.perTonFee)}/ton` : ''
  const parts = [incl, perTon].filter(Boolean)
  return parts.length ? parts.join(' · ') : '—'
}

export default function SubscriptionPlansPage() {
  const [includeInactive, setIncludeInactive] = useState(false)
  const [accountTypeFilter, setAccountTypeFilter] = useState<'ALL' | AccountType>('ALL')

  const [dialogOpen, setDialogOpen] = useState(false)
  const [editingPlan, setEditingPlan] = useState<SubscriptionPlanDto | null>(null)
  const [formData, setFormData] = useState<PlanFormState>(emptyForm)
  const [formError, setFormError] = useState('')

  const [duplicateFor, setDuplicateFor] = useState<SubscriptionPlanDto | null>(null)
  const [duplicateName, setDuplicateName] = useState('')
  const [duplicateCode, setDuplicateCode] = useState('')

  const [analyticsFor, setAnalyticsFor] = useState<SubscriptionPlanDto | null>(null)
  const [entitlementsFor, setEntitlementsFor] = useState<SubscriptionPlanDto | null>(null)

  const toast = useToast()
  const queryClient = useQueryClient()
  const getLocalizedLabel = useLocalizedLabel()

  const { data, isLoading, isError, isFetching, error, refetch } = useQuery({
    queryKey: ['subscription-plans', includeInactive],
    queryFn: () => subscriptionPlanService.getAll(includeInactive, 0, 200),
  })

  const analyticsQuery = useQuery({
    queryKey: ['plan-analytics', analyticsFor?.id],
    queryFn: () => subscriptionPlanService.getAnalytics(analyticsFor!.id),
    enabled: !!analyticsFor,
  })

  const activateMutation = useMutation({
    mutationFn: (planId: string) => subscriptionPlanService.activate(planId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['subscription-plans'] })
      toast.success('Plan activated')
    },
    onError: (e) => toast.errorFrom(e, 'Failed to activate'),
  })

  const deactivateMutation = useMutation({
    mutationFn: (planId: string) => subscriptionPlanService.deactivate(planId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['subscription-plans'] })
      toast.success('Plan deactivated')
    },
    onError: (e) => toast.errorFrom(e, 'Failed to deactivate'),
  })

  const createMutation = useMutation({
    mutationFn: (plan: CreatePlanRequest) => subscriptionPlanService.create(plan),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['subscription-plans'] })
      setDialogOpen(false)
      setFormError('')
      toast.success('Plan created')
    },
    onError: (error: any) =>
      setFormError(error?.response?.data?.message || error?.message || 'Failed to create plan'),
  })

  const updateMutation = useMutation({
    mutationFn: ({ planId, plan }: { planId: string; plan: UpdatePlanRequest }) =>
      subscriptionPlanService.update(planId, plan),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['subscription-plans'] })
      setDialogOpen(false)
      setFormError('')
      toast.success('Plan updated')
    },
    onError: (error: any) =>
      setFormError(error?.response?.data?.message || error?.message || 'Failed to update plan'),
  })

  const duplicateMutation = useMutation({
    mutationFn: ({ planId, newName, newCode }: { planId: string; newName: string; newCode: string }) =>
      subscriptionPlanService.duplicate(planId, newName, newCode),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['subscription-plans'] })
      setDuplicateFor(null)
      setDuplicateName('')
      setDuplicateCode('')
      toast.success('Plan duplicated')
    },
    onError: (e) => toast.errorFrom(e, 'Failed to duplicate'),
  })

  const handleOpenDialog = (plan: SubscriptionPlanDto | null) => {
    setEditingPlan(plan)
    setFormError('')
    if (plan) {
      setFormData({
        planCode: plan.planCode || '',
        name: plan.name || '',
        description: plan.description || '',
        accountType: plan.accountType || 'SHIPPER',
        billingCycle: plan.billingCycle || 'MONTHLY',
        monthlyFee: plan.monthlyFee != null ? String(plan.monthlyFee) : '0',
        currency: plan.currency || 'ILS',
        trialPeriodDays: plan.trialPeriodDays != null ? String(plan.trialPeriodDays) : '30',
        country: plan.country || 'Israel',
        isFeatured: !!plan.isFeatured,
        sortOrder: plan.sortOrder != null ? String(plan.sortOrder) : '0',
        features: plan.features || '',
        limitations: plan.limitations || '',
        maxJobsPerMonth: plan.maxJobsPerMonth != null ? String(plan.maxJobsPerMonth) : '',
        perJobCommission: plan.perJobCommission != null ? String(plan.perJobCommission) : '',
        includedTonsPerMonth:
          plan.includedTonsPerMonth != null ? String(plan.includedTonsPerMonth) : '',
        includedAreaM2PerMonth:
          plan.includedAreaM2PerMonth != null ? String(plan.includedAreaM2PerMonth) : '',
        includedVolumeM3PerMonth:
          plan.includedVolumeM3PerMonth != null ? String(plan.includedVolumeM3PerMonth) : '',
        perTonFee: plan.perTonFee != null ? String(plan.perTonFee) : '',
        perAreaM2Fee: plan.perAreaM2Fee != null ? String(plan.perAreaM2Fee) : '',
        perVolumeM3Fee: plan.perVolumeM3Fee != null ? String(plan.perVolumeM3Fee) : '',
        hasPriorityMatching: !!plan.hasPriorityMatching,
      })
    } else {
      setFormData(emptyForm)
    }
    setDialogOpen(true)
  }

  const handleSave = () => {
    if (!formData.name.trim()) {
      setFormError('Plan name is required')
      return
    }
    if (!editingPlan && !formData.planCode.trim()) {
      setFormError('Plan code is required when creating')
      return
    }
    if (!editingPlan && !formData.country.trim()) {
      setFormError('Country is required when creating')
      return
    }
    const monthlyFee = num(formData.monthlyFee) ?? 0

    const sharedBody = {
      name: formData.name.trim(),
      description: formData.description.trim() || undefined,
      monthlyFee,
      trialPeriodDays: num(formData.trialPeriodDays),
      isFeatured: formData.isFeatured,
      sortOrder: num(formData.sortOrder) ?? 0,
      features: formData.features || undefined,
      limitations: formData.limitations || undefined,
      ...(formData.accountType === 'FREIGHTER'
        ? {
            maxJobsPerMonth: num(formData.maxJobsPerMonth),
            perJobCommission: num(formData.perJobCommission),
          }
        : {
            includedTonsPerMonth: num(formData.includedTonsPerMonth),
            includedAreaM2PerMonth: num(formData.includedAreaM2PerMonth),
            includedVolumeM3PerMonth: num(formData.includedVolumeM3PerMonth),
            perTonFee: num(formData.perTonFee),
            perAreaM2Fee: num(formData.perAreaM2Fee),
            perVolumeM3Fee: num(formData.perVolumeM3Fee),
            hasPriorityMatching: formData.hasPriorityMatching,
          }),
    }

    if (editingPlan) {
      updateMutation.mutate({ planId: editingPlan.id, plan: sharedBody as UpdatePlanRequest })
    } else {
      const createBody: CreatePlanRequest = {
        ...(sharedBody as any),
        planCode: formData.planCode.trim(),
        accountType: formData.accountType,
        billingCycle: formData.billingCycle,
        // `price` is required on create (signup price); use the monthlyFee for both.
        price: monthlyFee,
        currency: formData.currency || 'ILS',
        country: formData.country.trim(),
      }
      createMutation.mutate(createBody)
    }
  }

  const allPlans: SubscriptionPlanDto[] = useMemo(
    () => data?.content ?? data ?? [],
    [data]
  )
  const visiblePlans = useMemo(
    () =>
      accountTypeFilter === 'ALL'
        ? allPlans
        : allPlans.filter((p) => p.accountType === accountTypeFilter),
    [allPlans, accountTypeFilter]
  )

  const handleOpenDuplicate = (plan: SubscriptionPlanDto) => {
    setDuplicateFor(plan)
    setDuplicateName(`${plan.name} (copy)`)
    setDuplicateCode(`${plan.planCode || 'PLAN'}_COPY`)
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
      <Stack
        direction="row"
        justifyContent="flex-end"
        alignItems="center"
        spacing={2}
        mb={3}
      >
        <Stack direction="row" spacing={2} alignItems="center">
          <TextField
            select
            size="small"
            label="Account type"
            value={accountTypeFilter}
            onChange={(e) => setAccountTypeFilter(e.target.value as any)}
            sx={{ minWidth: 160 }}
          >
            <MenuItem value="ALL">All</MenuItem>
            <MenuItem value="SHIPPER">Shipper</MenuItem>
            <MenuItem value="FREIGHTER">Freighter</MenuItem>
          </TextField>
          <FormControlLabel
            control={
              <Switch
                checked={includeInactive}
                onChange={(e) => setIncludeInactive(e.target.checked)}
              />
            }
            label="Include inactive"
          />
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog(null)}
          >
            Create Plan
          </Button>
          <PageRefreshButton onRefresh={() => refetch()} loading={isFetching} />
        </Stack>
      </Stack>

      {isLoading ? (
        <Paper sx={{ flex: 1, minHeight: 0 }}><TableSkeleton rows={6} columns={8} /></Paper>
      ) : isError ? (
        <Paper sx={{ flex: 1, minHeight: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <ErrorCard
            title="Failed to load plans"
            message={(error as Error)?.message}
            onRetry={() => refetch()}
          />
        </Paper>
      ) : (
        <TableContainer component={Paper} sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
          <Table sx={{ height: visiblePlans.length === 0 ? '100%' : undefined }}>
            <TableHead>
              <TableRow>
                <ResizableTableCell>Name</ResizableTableCell>
                <ResizableTableCell>Code</ResizableTableCell>
                <ResizableTableCell>Account Type</ResizableTableCell>
                <ResizableTableCell>Billing</ResizableTableCell>
                <ResizableTableCell>Monthly Fee</ResizableTableCell>
                <ResizableTableCell>Usage Pricing</ResizableTableCell>
                <ResizableTableCell>Trial</ResizableTableCell>
                <ResizableTableCell>Status</ResizableTableCell>
                <ResizableTableCell sx={stickyActionsHeaderSx}>Actions</ResizableTableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {visiblePlans.length === 0 && (
                <TableRow>
                  <TableCell colSpan={9} align="center" sx={{ borderBottom: 'none', verticalAlign: 'middle' }}>
                    <EmptyState
                      title="No plans match the current filters"
                      description="Adjust the account-type filter or include inactive plans."
                    />
                  </TableCell>
                </TableRow>
              )}
              {visiblePlans.map((plan) => (
                <TableRow key={plan.id} hover>
                  <TableCell>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <span>{plan.name}</span>
                      {plan.isFeatured && (
                        <Tooltip title="Featured">
                          <StarIcon fontSize="small" color="warning" />
                        </Tooltip>
                      )}
                    </Stack>
                    {plan.description && (
                      <Typography variant="caption" color="text.secondary" display="block">
                        {plan.description}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>
                    {plan.planCode || '—'}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={plan.accountType || '—'}
                      size="small"
                      color={plan.accountType === 'SHIPPER' ? 'info' : 'secondary'}
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>
                    {plan.billingCycle
                      ? getLocalizedLabel('billingCycle', plan.billingCycle) || plan.billingCycle
                      : '—'}
                  </TableCell>
                  <TableCell>
                    {plan.monthlyFee != null
                      ? `${formatCurrency(plan.monthlyFee)} ${plan.currency || ''}`
                      : '—'}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{planPricingSummary(plan)}</Typography>
                  </TableCell>
                  <TableCell>{plan.trialPeriodDays ?? 0}d</TableCell>
                  <TableCell>
                    <Chip
                      label={plan.isActive ? 'Active' : 'Inactive'}
                      color={plan.isActive ? 'success' : 'default'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell sx={stickyActionsBodySx}>
                    <Stack direction="row" spacing={0.5}>
                      <Tooltip title="Edit">
                        <IconButton size="small" onClick={() => handleOpenDialog(plan)}>
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Duplicate">
                        <IconButton size="small" onClick={() => handleOpenDuplicate(plan)}>
                          <ContentCopyIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Manage add-ons">
                        <IconButton size="small" onClick={() => setEntitlementsFor(plan)}>
                          <ExtensionIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="View analytics">
                        <IconButton size="small" onClick={() => setAnalyticsFor(plan)}>
                          <BarChartIcon />
                        </IconButton>
                      </Tooltip>
                      {plan.isActive ? (
                        <Button
                          size="small"
                          color="error"
                          onClick={() => deactivateMutation.mutate(plan.id)}
                          disabled={deactivateMutation.isPending}
                        >
                          Deactivate
                        </Button>
                      ) : (
                        <Button
                          size="small"
                          color="success"
                          onClick={() => activateMutation.mutate(plan.id)}
                          disabled={activateMutation.isPending}
                        >
                          Activate
                        </Button>
                      )}
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* ===== Create / Edit dialog ===== */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>{editingPlan ? `Edit Plan — ${editingPlan.name}` : 'Create New Plan'}</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ mt: 1 }}>
            {formError && <Alert severity="error">{formError}</Alert>}

            <Typography variant="subtitle2">Core</Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Plan Code *"
                  value={formData.planCode}
                  onChange={(e) => setFormData({ ...formData, planCode: e.target.value })}
                  fullWidth
                  size="small"
                  disabled={!!editingPlan}
                  helperText={editingPlan ? 'Immutable after creation' : 'Unique identifier'}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Name *"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  fullWidth
                  size="small"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  fullWidth
                  size="small"
                  multiline
                  rows={2}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  select
                  label="Account Type *"
                  value={formData.accountType}
                  onChange={(e) =>
                    setFormData({ ...formData, accountType: e.target.value as AccountType })
                  }
                  fullWidth
                  size="small"
                  disabled={!!editingPlan}
                  helperText={editingPlan ? 'Immutable after creation' : ''}
                >
                  <MenuItem value="SHIPPER">Shipper</MenuItem>
                  <MenuItem value="FREIGHTER">Freighter</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  select
                  label="Billing Cycle *"
                  value={formData.billingCycle}
                  onChange={(e) =>
                    setFormData({ ...formData, billingCycle: e.target.value as BillingCycle })
                  }
                  fullWidth
                  size="small"
                  disabled={!!editingPlan}
                  helperText={editingPlan ? 'Immutable after creation' : ''}
                >
                  <MenuItem value="MONTHLY">Monthly</MenuItem>
                  <MenuItem value="QUARTERLY">Quarterly</MenuItem>
                  <MenuItem value="YEARLY">Yearly</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={6} sm={4}>
                <TextField
                  label="Monthly Fee"
                  type="number"
                  value={formData.monthlyFee}
                  onChange={(e) => setFormData({ ...formData, monthlyFee: e.target.value })}
                  fullWidth
                  size="small"
                  inputProps={{ min: 0, step: 0.01 }}
                />
              </Grid>
              <Grid item xs={6} sm={4}>
                <TextField
                  label="Currency"
                  value={formData.currency}
                  onChange={(e) => setFormData({ ...formData, currency: e.target.value.toUpperCase() })}
                  fullWidth
                  size="small"
                  inputProps={{ maxLength: 3 }}
                  disabled={!!editingPlan}
                />
              </Grid>
              <Grid item xs={6} sm={4}>
                <TextField
                  label="Country"
                  value={formData.country}
                  onChange={(e) => setFormData({ ...formData, country: e.target.value })}
                  fullWidth
                  size="small"
                  disabled={!!editingPlan}
                />
              </Grid>
              <Grid item xs={6} sm={3}>
                <TextField
                  label="Trial Days"
                  type="number"
                  value={formData.trialPeriodDays}
                  onChange={(e) => setFormData({ ...formData, trialPeriodDays: e.target.value })}
                  fullWidth
                  size="small"
                  inputProps={{ min: 0 }}
                />
              </Grid>
              <Grid item xs={6} sm={3}>
                <TextField
                  label="Sort Order"
                  type="number"
                  value={formData.sortOrder}
                  onChange={(e) => setFormData({ ...formData, sortOrder: e.target.value })}
                  fullWidth
                  size="small"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.isFeatured}
                      onChange={(e) =>
                        setFormData({ ...formData, isFeatured: e.target.checked })
                      }
                    />
                  }
                  label="Featured"
                />
              </Grid>
            </Grid>

            <Typography variant="subtitle2" sx={{ mt: 1 }}>
              {formData.accountType === 'FREIGHTER' ? 'Freighter Limits' : 'Shipper Limits'}
            </Typography>
            {formData.accountType === 'FREIGHTER' ? (
              <Grid container spacing={2}>
                <Grid item xs={6} sm={6}>
                  <TextField
                    label="Max Jobs / Month"
                    type="number"
                    value={formData.maxJobsPerMonth}
                    onChange={(e) =>
                      setFormData({ ...formData, maxJobsPerMonth: e.target.value })
                    }
                    fullWidth
                    size="small"
                    inputProps={{ min: 0 }}
                  />
                </Grid>
                <Grid item xs={6} sm={6}>
                  <TextField
                    label="Per-Job Commission"
                    type="number"
                    value={formData.perJobCommission}
                    onChange={(e) =>
                      setFormData({ ...formData, perJobCommission: e.target.value })
                    }
                    fullWidth
                    size="small"
                    inputProps={{ min: 0, step: 0.01 }}
                  />
                </Grid>
              </Grid>
            ) : (
              <Grid container spacing={2}>
                <Grid item xs={4}>
                  <TextField
                    label="Included tons / mo"
                    type="number"
                    value={formData.includedTonsPerMonth}
                    onChange={(e) =>
                      setFormData({ ...formData, includedTonsPerMonth: e.target.value })
                    }
                    fullWidth
                    size="small"
                  />
                </Grid>
                <Grid item xs={4}>
                  <TextField
                    label="Included m² / mo"
                    type="number"
                    value={formData.includedAreaM2PerMonth}
                    onChange={(e) =>
                      setFormData({ ...formData, includedAreaM2PerMonth: e.target.value })
                    }
                    fullWidth
                    size="small"
                  />
                </Grid>
                <Grid item xs={4}>
                  <TextField
                    label="Included m³ / mo"
                    type="number"
                    value={formData.includedVolumeM3PerMonth}
                    onChange={(e) =>
                      setFormData({ ...formData, includedVolumeM3PerMonth: e.target.value })
                    }
                    fullWidth
                    size="small"
                  />
                </Grid>
                <Grid item xs={4}>
                  <TextField
                    label="Per-ton fee"
                    type="number"
                    value={formData.perTonFee}
                    onChange={(e) =>
                      setFormData({ ...formData, perTonFee: e.target.value })
                    }
                    fullWidth
                    size="small"
                  />
                </Grid>
                <Grid item xs={4}>
                  <TextField
                    label="Per-m² fee"
                    type="number"
                    value={formData.perAreaM2Fee}
                    onChange={(e) =>
                      setFormData({ ...formData, perAreaM2Fee: e.target.value })
                    }
                    fullWidth
                    size="small"
                  />
                </Grid>
                <Grid item xs={4}>
                  <TextField
                    label="Per-m³ fee"
                    type="number"
                    value={formData.perVolumeM3Fee}
                    onChange={(e) =>
                      setFormData({ ...formData, perVolumeM3Fee: e.target.value })
                    }
                    fullWidth
                    size="small"
                  />
                </Grid>
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.hasPriorityMatching}
                        onChange={(e) =>
                          setFormData({ ...formData, hasPriorityMatching: e.target.checked })
                        }
                      />
                    }
                    label="Priority matching"
                  />
                </Grid>
              </Grid>
            )}

            <Typography variant="subtitle2" sx={{ mt: 1 }}>
              Content
            </Typography>
            <TextField
              label="Features"
              value={formData.features}
              onChange={(e) => setFormData({ ...formData, features: e.target.value })}
              fullWidth
              size="small"
              multiline
              rows={3}
              helperText="JSON array or free text — backend stores as-is"
            />
            <TextField
              label="Limitations"
              value={formData.limitations}
              onChange={(e) => setFormData({ ...formData, limitations: e.target.value })}
              fullWidth
              size="small"
              multiline
              rows={3}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={createMutation.isPending || updateMutation.isPending}
          >
            {createMutation.isPending || updateMutation.isPending ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ===== Duplicate dialog ===== */}
      <Dialog
        open={!!duplicateFor}
        onClose={() => setDuplicateFor(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Duplicate Plan</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Source: <strong>{duplicateFor?.name}</strong> ({duplicateFor?.planCode})
            </Typography>
            <TextField
              label="New name *"
              value={duplicateName}
              onChange={(e) => setDuplicateName(e.target.value)}
              fullWidth
              size="small"
            />
            <TextField
              label="New plan code *"
              value={duplicateCode}
              onChange={(e) => setDuplicateCode(e.target.value)}
              fullWidth
              size="small"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDuplicateFor(null)}>Cancel</Button>
          <Button
            variant="contained"
            startIcon={<ContentCopyIcon />}
            disabled={
              !duplicateFor ||
              !duplicateName.trim() ||
              !duplicateCode.trim() ||
              duplicateMutation.isPending
            }
            onClick={() =>
              duplicateFor &&
              duplicateMutation.mutate({
                planId: duplicateFor.id,
                newName: duplicateName.trim(),
                newCode: duplicateCode.trim(),
              })
            }
          >
            Duplicate
          </Button>
        </DialogActions>
      </Dialog>

      {/* ===== Analytics dialog ===== */}
      <Dialog
        open={!!analyticsFor}
        onClose={() => setAnalyticsFor(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Plan Analytics — {analyticsFor?.name}</DialogTitle>
        <DialogContent dividers>
          {analyticsQuery.isLoading && (
            <Box display="flex" justifyContent="center" py={3}>
              <CircularProgress />
            </Box>
          )}
          {analyticsQuery.data && (
            <pre
              style={{
                margin: 0,
                fontFamily: 'inherit',
                whiteSpace: 'pre-wrap',
                fontSize: '0.85rem',
              }}
            >
              {JSON.stringify(analyticsQuery.data, null, 2)}
            </pre>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAnalyticsFor(null)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Per-plan add-on (entitlement) manager */}
      <PlanEntitlementsDialog
        plan={entitlementsFor}
        open={!!entitlementsFor}
        onClose={() => setEntitlementsFor(null)}
      />
    </Box>
  )
}
